import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { withSite, formatErr } from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import ExportButton from "@/components/ExportButton";
import PdfButton from "@/components/PdfButton";
import BarcodeScanner from "@/components/BarcodeScanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, Plus, ScanLine, XCircle } from "lucide-react";

export default function Cleaning() {
  const { activeSite } = useSite();
  const { user } = useAuth();
  const canEdit = user?.role !== "auditor";
  const isAdmin = user?.role === "admin";

  const [tasks, setTasks] = useState([]);
  const [logs, setLogs] = useState([]);
  const [newTask, setNewTask] = useState({ name: "", area: "", frequency: "daily", chemical: "", method: "" });
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannedEquipment, setScannedEquipment] = useState(null);
  const [searchParams] = useSearchParams();

  // Honour ?equipment=<id> deep-links from the PWA Scan shortcut — the
  // Cleaning page should open with the task list already filtered to that
  // piece of kit so the user can sign off without scanning again.
  useEffect(() => {
    const eid = searchParams.get("equipment");
    if (!eid || !activeSite) return;
    if (scannedEquipment && scannedEquipment.id === eid) return;
    api
      .get("/equipment", withSite(activeSite.id))
      .then(({ data }) => {
        if (!data) return;
        const match = data.find((e) => e.id === eid);
        if (match) setScannedEquipment(match);
      })
      .catch(() => {});
  }, [searchParams, activeSite, scannedEquipment]);

  const filteredTasks = useMemo(() => {
    if (!scannedEquipment) return tasks;
    const needle = (scannedEquipment.location || scannedEquipment.name || "").toLowerCase();
    if (!needle) return tasks;
    const match = tasks.filter((t) =>
      (t.area || "").toLowerCase().includes(needle) ||
      needle.includes((t.area || "").toLowerCase()) ||
      (t.name || "").toLowerCase().includes(needle),
    );
    return match.length ? match : tasks;
  }, [tasks, scannedEquipment]);

  const handleScan = async (code) => {
    setScannerOpen(false);
    if (!activeSite || !code) return;
    try {
      const { data } = await api.get("/equipment/by-barcode", {
        params: { code, site_id: activeSite.id },
      });
      if (!data) return;
      setScannedEquipment(data);
      toast.success(`Filtered to ${data.name} (${data.location || "this area"})`);
    } catch (err) {
      toast.error(
        formatErr(err.response?.data?.detail) || `No equipment matches "${code}"`,
      );
    }
  };

  const load = async () => {
    if (!activeSite) return;
    const [t, l] = await Promise.all([
      api.get("/cleaning/tasks", withSite(activeSite.id)),
      api.get("/cleaning/logs", withSite(activeSite.id)),
    ]);
    setTasks(t.data);
    setLogs(l.data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSite]);

  const addTask = async (e) => {
    e.preventDefault();
    try {
      await api.post("/cleaning/tasks", newTask, withSite(activeSite.id));
      toast.success("Task added");
      setNewTask({ name: "", area: "", frequency: "daily", chemical: "", method: "" });
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const signOff = async (task) => {
    try {
      const notes = scannedEquipment ? `Scanned: ${scannedEquipment.name}` : "";
      await api.post(
        "/cleaning/logs",
        { task_id: task.id, completed: true, verified_by: user.name, notes },
        withSite(activeSite.id)
      );
      toast.success(`Signed off: ${task.name}`);
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  return (
    <div className="animate-fade-in" data-testid="cleaning-page">
      <PageHeader
        title="Cleaning & Sanitation"
        subtitle="Master cleaning schedule and verification sign-offs. Set up tasks once — then tap to sign off on completion."
        action={
          <div className="flex items-center gap-2">
            <ExportButton kind="cleaning" />
            <PdfButton kind="cleaning" />
          </div>
        }
      />

      <Tabs defaultValue="schedule">
        <TabsList className="mb-6">
          <TabsTrigger data-testid="tab-schedule" value="schedule">Schedule</TabsTrigger>
          <TabsTrigger data-testid="tab-signoffs" value="signoffs">Sign-off history</TabsTrigger>
        </TabsList>

        <TabsContent value="schedule">
          <div className="flex flex-wrap gap-3 items-center justify-between mb-4">
            <Button
              type="button"
              data-testid="clean-scan-equipment"
              onClick={() => setScannerOpen(true)}
              className="h-12 bg-ochre-600 hover:bg-ochre-400 text-white gap-2"
            >
              <ScanLine className="w-4 h-4" /> Scan equipment
            </Button>
            {scannedEquipment && (
              <div
                data-testid="clean-scan-active"
                className="flex items-center gap-2 surface-alt px-3 py-2 text-sm"
              >
                <span className="font-mono text-xs text-charcoal-soft">FILTER</span>
                <span className="font-bold">{scannedEquipment.name}</span>
                <button
                  data-testid="clean-scan-clear"
                  onClick={() => setScannedEquipment(null)}
                  className="text-ochre-600 hover:text-ochre-400"
                  aria-label="Clear scan filter"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          {canEdit && (
            <form onSubmit={addTask} className="surface p-6 mb-6 grid md:grid-cols-5 gap-3">
              <Input
                data-testid="ct-name"
                placeholder="Task name"
                value={newTask.name}
                onChange={(e) => setNewTask({ ...newTask, name: e.target.value })}
                className="h-12"
                required
              />
              <Input
                data-testid="ct-area"
                placeholder="Area"
                value={newTask.area}
                onChange={(e) => setNewTask({ ...newTask, area: e.target.value })}
                className="h-12"
              />
              <Select value={newTask.frequency} onValueChange={(v) => setNewTask({ ...newTask, frequency: v })}>
                <SelectTrigger data-testid="ct-freq" className="h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                </SelectContent>
              </Select>
              <Input
                data-testid="ct-chemical"
                placeholder="Chemical / conc."
                value={newTask.chemical}
                onChange={(e) => setNewTask({ ...newTask, chemical: e.target.value })}
                className="h-12"
              />
              <Button
                type="submit"
                data-testid="ct-submit"
                className="h-12 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white gap-1"
              >
                <Plus className="w-4 h-4" /> Add task
              </Button>
            </form>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {filteredTasks.map((t) => (
              <div key={t.id} data-testid={`task-${t.id}`} className="surface p-5 flex items-start justify-between gap-4">
                <div>
                  <div className="label-eyebrow">{t.frequency}</div>
                  <div className="font-display font-bold mt-1">{t.name}</div>
                  <div className="text-xs text-charcoal-soft mt-1">{t.area}</div>
                  {t.chemical && (
                    <div className="text-xs font-mono mt-2">{t.chemical}</div>
                  )}
                </div>
                {canEdit && (
                  <Button
                    data-testid={`signoff-${t.id}`}
                    onClick={() => signOff(t)}
                    className="bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white gap-2"
                  >
                    <CheckCircle2 className="w-4 h-4" /> Sign off
                  </Button>
                )}
              </div>
            ))}
            {filteredTasks.length === 0 && (
              <div className="surface p-10 text-center text-charcoal-soft md:col-span-2">
                No cleaning tasks yet.
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="signoffs">
          <div className="surface overflow-hidden">
            <div className="grid grid-cols-12 bg-bone-alt px-6 py-3 text-[11px] uppercase font-bold tracking-widest text-eucalyptus-700">
              <div className="col-span-5">Task</div>
              <div className="col-span-2 text-center">Status</div>
              <div className="col-span-2">Verified by</div>
              <div className="col-span-3">When</div>
            </div>
            {logs.length === 0 ? (
              <div className="p-10 text-center text-charcoal-soft text-sm">No sign-offs yet.</div>
            ) : (
              logs.map((l) => (
                <div key={l.id} className="grid grid-cols-12 px-6 py-4 border-t border-[color:var(--border)]">
                  <div className="col-span-5 font-medium">{l.task_name}</div>
                  <div className="col-span-2 text-center">
                    <Badge className="bg-eucalyptus-100 text-eucalyptus-900 hover:bg-eucalyptus-100">Done</Badge>
                  </div>
                  <div className="col-span-2 text-sm">{l.verified_by || l.user_name}</div>
                  <div className="col-span-3 text-xs font-mono text-charcoal-soft">
                    {new Date(l.created_at).toLocaleString("en-AU")}
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      <BarcodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onResult={handleScan}
        title="Scan equipment to filter tasks"
      />
    </div>
  );
}
